<script>
  import { navigate } from "svelte-routing";
  import { onMount, getContext, setContext, onDestroy, tick } from "svelte";
  import { writable, derived } from "svelte/store";
  import { downloadString } from "downloaders";
  import { Portal, tooltip } from "svelte-utilities";
  import uniqBy from "lodash/uniqBy";
  import debounce from "lodash/debounce";
  import cloneDeep from "lodash/cloneDeep";

  import BusinessIcon from "@local/assets/icons/business.svg";
  import ClockIcon from "@local/assets/icons/clock.svg";
  import LocationIcon from "@local/assets/icons/location.svg";
  import NotesIcon from "@local/assets/icons/notes.svg";
  import NumberIcon from "@local/assets/icons/number.svg";
  import HashIcon from "@local/assets/icons/hash.svg";
  import DownloadIcon from "@local/assets/icons/download.svg";
  import GearIcon from "@local/assets/icons/gear.svg";
  import HamburgerMenu from "../HamburgerMenu.svelte";
  import HamburgerMenuItem from "../HamburgerMenuItem.svelte";
  import ProfileIcon from "@local/assets/icons/profile.svg";
  import DownloadModal from "./DownloadModal.svelte";

  import SearchSelect from "#lib/sidebar/SearchSelect.svelte";
  import SelectInput from "#lib/sidebar/SelectInput.svelte";
  import BooleanInput from "#lib/sidebar/BooleanInput.svelte";
  import ContactInput from "#lib/sidebar/ContactInput.svelte";
  import TextInput from "#lib/sidebar/TextInput.svelte";
  import DocumentsInput from "#lib/home/DocumentsInput.svelte";
  import GroupAttachments from "./GroupAttachments.svelte";
  import ContenteditableInput from "#lib/sidebar/ContenteditableInput.svelte";
  import MapboxInput from "#lib/sidebar/MapboxInput.svelte";
  import ReportTable from "./ReportTable.svelte";
  import ProductSummaryTable from "./ProductSummaryTable.svelte";
  import GroupSummary from "./GroupSummary.svelte";
  import ShareDraftModal from "./ShareDraftModal.svelte";
  import ProductSendModal from "./ProductSendModal.svelte";
  import HomeSettingsModal from "./HomeSettingsModal.svelte";
  import PriceComputer from "./PriceComputer.svelte";
  import SelectedActions from "#lib/SelectedActions.svelte";

  import { orderedList } from "@local/extensions/collections/sortable-list.js";
  import { profile } from "#src/stores/auth.js";
  import { jobs, fetchJob, selectedJobIds } from "#src/stores/jobs.js";
  import { quotes, selectedQuoteIds } from "#src/stores/quotes.js";
  import { fetchGroup, createGroup } from "#src/stores/group.js";

  import { typeColor, typeColorTrue } from "@local/extensions/drawing/type-color.js";
  import { bucketArrayMultiple, objectify } from "overline";
  import sanitizePgString from "#src/extensions/sanitize-postgrest-string.js";
  import getAssignToOptions from "#src/extensions/assign-to-options.js";
  import { exportJson } from "#src/extensions/export-job/json.js";
  import { currentSupplierid } from "#src/stores/ui.js";
  import eb from "#src/extensions/event-bus.js";
  import { api } from "#src/api";
  import { selfSendRequest } from "#src/api/requests.js";
  import { orgLibrary } from "#src/extensions/makeup-library.js";
  import { parseQuoteScript } from "#src/extensions/importers/item-script.js";
  import { pricingGroupProps, getItemPrices, prepareReportData } from "@local/lamina-core";
  import { formatColumns } from "#src/extensions/format-columns.js";
  import { alerts } from "#src/stores/alerts.js";

  export let jobid;

  const org = getContext("org");
  const productLists = getContext("productLists");
  const priceEntries = getContext("priceEntries");
  const selectedProducts = getContext("selectedProducts");
  const supplier = getContext("supplier");
  const typeColors = writable({});
  const typeColorsTrue = writable({});

  let job;
  let group;
  let customerSearchString = "";
  let selectedCustomer = null;
  let customers = [];

  const makeupLibrary = derived(supplier, ($supplier) => orgLibrary($supplier, $group?.project_type));

  setContext("typeColors", typeColors);
  setContext("typeColorsTrue", typeColorsTrue);
  setContext("makeupLibrary", makeupLibrary);

  function transform(coll, xf, defaultValue = []) {
    return coll ? xf(coll) : defaultValue;
  }

  const paramInputs = {
    text: TextInput,
    boolean: BooleanInput,
    select: SelectInput,
  };

  const paramIcons = {
    number: NumberIcon,
    text: NotesIcon,
    business: BusinessIcon,
    clock: ClockIcon,
    location: LocationIcon,
  };

  $: archived = job?.archived_at;
  $: address = job?.location?.split("\n") || [];
  $: addressFirstLine = address[0];
  $: addressRest = address.slice(1);
  $: assignToOptions = getAssignToOptions($profile, $org);
  $: allAttachments = $group && bucketArrayMultiple($group.attachments, "item_id", "type_id");
  $: selectedProductsList = Object.keys($selectedProducts)
    .map((id) => $group?.items[id])
    .filter((i) => i);
  $: selectedQuoteItemsList = Object.keys($selectedProducts)
    .map((id) => $group?.quote_items.find((qi) => qi.id === id))
    .filter((i) => i);

  $: items = transform($group?.items, orderedList).filter((i) => !i.is_collection);
  $: collections = transform($group?.items, orderedList).filter((i) => i.is_collection);
  $: types = transform($group?.types, orderedList);
  $: documentNotes = transform($group?.document_notes, orderedList);
  $: $typeColors = types.reduce((a, t) => {
    a[t.id] = typeColor(t);
    return a;
  }, {});
  $: $typeColorsTrue = types.reduce((a, t) => {
    a[t.id] = typeColorTrue(t);
    return a;
  }, {});

  $: customerResults = customers.map((customer) => ({
    id: customer.id,
    name: customer.name,
    // cta: customer.primary_contact_email,
  }));
  $: plOptions = $productLists.map((pl) => ({ label: pl.name, value: pl.id }));
  $: currentPriceList = getCurrentPriceList(job);
  $: jobProperties = $group && pricingGroupProps($group);
  $: itemPrices = getItemPrices($group, currentPriceList, $priceEntries, jobProperties);
  $: quoteScripts = getQuoteScripts(currentPriceList);
  $: orgColumns = transform($org?.data?.custom_columns, orderedList);
  $: jobColumns = transform($group?.data?.custom_columns, orderedList);
  $: standardColumns = $org?.data?.standard_columns || {};
  $: columns = $group ? formatColumns($group, types, orgColumns, jobColumns, standardColumns) : [];
  $: reports = orderedList($group?.item_reports || { order: [] });
  $: reportData = prepareReportData(items, collections, types, documentNotes, $group, null, columns);
  $: allCols = reportData.totals.has_custom_shape
    ? [...columns, { label: "Shape", prop: "is_custom_shape" }]
    : columns;

  async function updateJob(update) {
    await api.from("jobs").update(update).eq("id", jobid).select("*").single();

    job = await fetchJob(jobid);
    jobs.fetchOne(jobid);
  }

  function updateContact(e) {
    const { contact, organization, postal_code, country_code } = e.detail;
    const customer_record = contact ? { organization_address: organization?.address, ...contact } : null;

    if (!job.location) {
      updateJob({
        customer_record,
        location: organization?.address || null,
        location_postal_code: postal_code || null,
        location_country_code: organization?.address ? country_code || "USA" : null,
      });
    } else {
      updateJob({ customer_record });
    }
  }

  function updateCustomJobParam(param, value) {
    const data = cloneDeep(job.custom_param_data) || {};
    data[param] = value;
    updateJob({ custom_param_data: data });
  }

  function sumArea(items) {
    return items.reduce((acc, item) => {
      return acc + (item.cache?.area || 0);
    }, 0);
  }

  async function updateGroup(prop, value) {
    group.updateProp(prop, value);
  }

  function getCurrentPriceList(job) {
    if (!job) return null;
    if (!$productLists) return null;
    return $productLists.find((pl) => pl.id === job.product_list_id);
  }

  function getQuoteScripts(priceList) {
    if (!priceList) return [];

    return priceList.quote_item_scripts.map((script) => {
      return { id: script.id, script: parseQuoteScript(script.script) };
    });
  }

  async function groupUpdateCallback() {
    const quoteItems = [];
    await tick();
    const productSettings = objectify(
      $supplier?.products.filter((p) => p.application === "product_setting") || [],
    );
    quoteScripts.forEach((script) => {
      const u = script.script.run($group, {
        controlled_by_id: script.id,
        item_prices: itemPrices,
        settings: productSettings,
      });
      quoteItems.push(...u);
    });
    await group.postUpdate(quoteItems);
  }

  async function downloadJSON() {
    const json = exportJson($group, job);
    downloadString(json, `${job.name}.json`);
  }

  async function archiveJob() {
    await jobs.archive(jobid);
  }

  async function unarchiveJob() {
    await jobs.unarchive(jobid);
    job = await fetchJob(jobid);
    jobs.fetchOne(jobid);
  }

  async function fetchCustomers() {
    const s = sanitizePgString(customerSearchString);

    const c = await api
      .from("business_relation_list_view")
      .select("*,buyer:buyer_id(*)")
      .eq("seller_id", $org.id)
      .or(`recipient_name.ilike.%${s}%,recipient_email.ilike.%${s}%`)
      .range(0, 8);

    if (!c.error) {
      customers = uniqBy(c.data, "buyer_id").map((c) => c.buyer);
    }
  }

  const searchCustomers = debounce(fetchCustomers, 500);

  function selectCustomer(e) {
    updateJob({ prepared_for_org_id: e.detail.result.id });
    selectedCustomer = e.detail.result;
  }

  function deselectCustomer() {
    updateJob({ prepared_for_org_id: null });
    selectedCustomer = null;
    customerSearchString = "";
    fetchCustomers();
  }

  async function selfSend(e) {
    jobs.spin(jobid);
    const quote = await selfSendRequest({ job_id: jobid, sender_id: $profile.id });
    if (quote.error) {
      if (quote.error.status === "invalid_deal_id") {
        alerts.add({ type: "error", message: "Invalid deal ID." });
      } else {
        alerts.add({ type: "error", message: "Failed to create quote." });
      }
      console.log(quote.error);
      jobs.unspin(jobid);
      return;
    }
    await quotes.fetchOne(quote.data.id);
    $selectedJobIds = [];
    $selectedQuoteIds = [quote.data.id];
    jobs.unloadOne(jobid);
  }

  function openJobSettings() {
    eb.dispatch("open-home-settings-modal");
  }

  function deleteSelected() {
    const ids = Object.keys($selectedProducts);
    if (ids.some((id) => $group.items[id])) {
      group.removeItem(...ids);
    } else {
      group.removeQuoteItem(...ids);
    }
  }

  function cloneSelected() {
    const ids = Object.keys($selectedProducts);
    const cloned = ids
      .map((id) => $group.items[id])
      .map((item) => ({
        ...item,
        id: crypto.randomUUID(),
      }));
    const types = cloned
      .map((item) => $group.types[item.type_id])
      .map((type) => ({
        ...type,
        id: crypto.randomUUID(),
      }));

    cloned.forEach((item, idx) => {
      item.type_id = types[idx].id;
    });

    const updates = [
      { type: "type", action: "add", records: types, block: true },
      { type: "item", action: "add", records: cloned },
    ];
    group.update(updates);
  }

  onMount(async () => {
    if (!jobid) return;
    fetchCustomers();

    try {
      job = await fetchJob(jobid);
      const gd = await fetchGroup(job?.current_group_id);
      group = createGroup(gd, { updateCallback: gd.project_type === "product" ? groupUpdateCallback : null });
      if (job?.prepared_for_org_id) {
        const customer = await api
          .from("organizations")
          .select("*")
          .eq("id", job.prepared_for_org_id)
          .single();

        if (customer.data) {
          selectedCustomer = {
            id: customer.data.id,
            name: customer.data.name,
          };
        }
      }
      $currentSupplierid = $org.id;
    } catch (error) {
      console.error(error);
    }
  });

  onDestroy(() => {
    if (group) group.destroy();
  });
</script>

{#if job}
  <div class="relative w-full h-full flex flex-col">
    <div class="flex-none flex justify-between items-start border-b pb-4 mx-4">
      <div class="grow flex items-start gap-2">
        <div>
          <div class="font-bold w-48 truncate">
            {addressFirstLine || ""}&nbsp;
          </div>
          <div class="text-xs">
            {#each addressRest as line}
              <div>{line}</div>
            {/each}
          </div>
        </div>
        <GroupSummary {group} {items} {collections} {types} report={reports[0]} data={reportData} />
      </div>

      <div class="flex gap-2">
        {#if $profile?.user_role === "developer"}
          <button class="btn btn-icon" on:click={() => eb.dispatch("open-download")}>
            <DownloadIcon />
          </button>
        {/if}
        <div class="btn-bare">
          <HamburgerMenu>
            {#if archived}
              <HamburgerMenuItem label="Unarchive" icon="unarchive" on:click={unarchiveJob} />
            {:else}
              <HamburgerMenuItem label="Archive" icon="archive" on:click={archiveJob} />
            {/if}
            {#if $profile?.profile_capabilities?.capabilities?.download_json}
              <HamburgerMenuItem label="Download JSON" icon="download" on:click={downloadJSON} />
            {/if}
            <HamburgerMenuItem label="Settings" on:click={openJobSettings}>
              <GearIcon />
              <div class="whitespace-nowrap">Settings</div>
            </HamburgerMenuItem>
          </HamburgerMenu>
        </div>
      </div>
    </div>

    <div class="grow flex">
      <div class="relative content-columns grow">
        <div class="left-column p-4 space-y-2">
          <div class="content space-y-2">
            <!-- {#if $profile?.user_role !== "product_user" && customers.length}
                <SearchSelect
                  placeholder="Customer Org"
                  label="Customer"
                  labelWidth="1rem"
                  outerBorder={false}
                  searchIcon={false}
                  bind:value={customerSearchString}
                  selection={selectedCustomer}
                  results={customerResults}
                  on:select={selectCustomer}
                  on:deselect={deselectCustomer}
                  on:search={searchCustomers}>
                  <div class="text-black" slot="label">
                    <BusinessIcon />
                  </div>
                </SearchSelect>
              {/if} -->
            {#if $group?.project_type === "product"}
              {#if $org.crm_integration_id}
                <ContactInput
                  label={null}
                  labelWidth="1rem"
                  value={job.customer_record}
                  on:input={updateContact}
                  outerBorder={false}
                  placeholder="Customer"
                  orgid={$org.id}>
                  <div class="text-black" slot="label">
                    <ProfileIcon />
                  </div>
                </ContactInput>
              {:else}
                <TextInput
                  labelWidth="1rem"
                  value={job.customer_text}
                  on:input={(e) => updateJob({ customer_text: e.detail.value })}
                  outerBorder={false}
                  placeholder="Customer">
                  <div class="text-black" slot="label">
                    <ProfileIcon />
                  </div>
                </TextInput>
              {/if}
            {/if}
            <MapboxInput
              labelWidth="1rem"
              value={job.location}
              coordinates={job.location_coordinates}
              outerBorder={false}
              placeholder="Location"
              on:input={(e) =>
                updateJob({
                  location: e.detail.value,
                  location_coordinates: e.detail.coordinates,
                  location_postal_code: e.detail.postal_code,
                  location_country_code: e.detail.country_code,
                })}>
              <div class="text-black" slot="label">
                <LocationIcon />
              </div>
            </MapboxInput>
            {#if $supplier?.custom_job_params}
              {#each $supplier.custom_job_params as param}
                <svelte:component
                  this={paramInputs[param.type]}
                  outerBorder={false}
                  labelWidth="1rem"
                  value={job.custom_param_data?.[param.id] ?? param.default}
                  options={param.options}
                  placeholder={param.label}
                  on:input={(e) => updateCustomJobParam(param.id, e.detail.value)}>
                  <div class="text-black" slot="label">
                    <svelte:component this={paramIcons[param.icon]} />
                  </div>
                </svelte:component>
              {/each}
            {/if}
            <GroupAttachments {group} attachments={allAttachments?.none} />
          </div>
          <div class="content space-y-2">
            <SelectInput
              labelWidth="1rem"
              options={assignToOptions}
              placeholder="Unassigned"
              value={job.assigned_to_id}
              outerBorder={false}
              showCaret
              on:input={(e) => updateJob({ assigned_to_id: e.detail.value })}>
              <div class="text-black" slot="label"><ProfileIcon /></div>
              <div slot="selected" let:option class="flex items-center gap-1">
                {#if option.initials}
                  <div
                    class="profile-circle relative"
                    style="background-color: {option.color || '#000'}; border-color: {option.color ||
                      '#000'}">
                    {option.initials}
                  </div>
                {/if}
                <div class:text-gray-400={!option.value}>
                  {option.label}
                </div>
              </div>
              <div slot="option" let:option class="flex items-center gap-1 overflow-hidden">
                {#if option.initials}
                  <div
                    class="profile-circle relative"
                    style="background-color: {option.color || '#000'}; border-color: {option.color ||
                      '#000'}">
                    {option.initials}
                  </div>
                {:else}
                  <div class="flex-none h-4 w-4" />
                {/if}
                <div class:text-gray-400={!option.value} class="truncate">
                  {option.label}
                </div>
              </div>
            </SelectInput>
            {#if $group?.project_type === "product"}
              <TextInput
                labelWidth="1rem"
                value={$group.seller_reference}
                outerBorder={false}
                disabled
                placeholder="PO Number">
                <div class="text-black" slot="label" use:tooltip={{ text: "PO Number" }}>
                  <NumberIcon />
                </div>
              </TextInput>
              {#if $org.crm_integration_id}
                <TextInput
                  labelWidth="1rem"
                  value={job.crm_deal_reference}
                  outerBorder={false}
                  on:input={(e) => updateJob({ crm_deal_reference: e.detail.value })}
                  placeholder="Deal ID">
                  <div class="text-black" slot="label" use:tooltip={{ text: "Deal ID" }}>
                    <HashIcon />
                  </div>
                </TextInput>
              {/if}
            {/if}
            {#if job.quote_form_request_id}
              <div class="flex gap-2">
                <div>
                  <NumberIcon />
                </div>
                <div class="px-1">
                  <a
                    href="http://localhost:5174/dashboard?quote={job.quote_form_request_id}"
                    class="text-blue-500 hover:underline"
                    target="_blank">
                    {job.quote_form_request.reference}
                  </a>
                </div>
              </div>
            {/if}
            {#if $group?.project_type === "product"}
              <SelectInput
                labelWidth="1rem"
                outerBorder={false}
                value={job.product_list_id}
                options={plOptions}
                on:input={(e) => updateJob({ product_list_id: e.detail.value })}>
                <div class="text-black text-center" slot="label" use:tooltip={{ text: "Price List" }}>$</div>
              </SelectInput>
            {/if}
            <ContenteditableInput
              placeholder="Notes"
              labelWidth="1rem"
              value={job.notes}
              outerBorder={false}
              on:input={(e) => updateJob({ notes: e.detail.value })}>
              <div class="text-black" slot="label">
                <NotesIcon />
              </div>
            </ContenteditableInput>
            {#if $group?.project_type === "unconstrained"}
              <DocumentsInput {group} />
            {/if}
          </div>
        </div>

        <div class="center-column space-y-2">
          {#if $group?.project_type === "product"}
            <ProductSummaryTable
              recordId={jobid}
              recordType="job"
              sent={false}
              {group}
              {types}
              {items}
              {itemPrices}
              {org}
              quote={null}
              {job} />
          {:else if $group}
            {#if !items.length}
              <div class="flex gap-2 text-xs px-2 justify-center mb-36">
                <div class="italic py-1">This job contains zero items.</div>
                <button class="text-blue-500" on:click={() => navigate(`/jobs/${jobid}/current`)}
                  >Add Items</button>
              </div>
            {:else}
              <ReportTable
                sent={false}
                data={reportData}
                columns={allCols}
                {group}
                attachments={allAttachments} />
            {/if}
          {/if}
        </div>
        {#if selectedProductsList.length || selectedQuoteItemsList.length}
          <Portal target="#job-detail-header-container">
            <SelectedActions
              cloneable={!selectedQuoteItemsList.length}
              deletable
              selected={selectedProductsList.length + selectedQuoteItemsList.length}
              on:delete={deleteSelected}
              on:clone={cloneSelected} />
          </Portal>
        {/if}
      </div>
      {#if $org?.show_price_calculator && $group?.project_type === "product"}
        <div class="price-computer">
          <PriceComputer {group} {items} {types} {job} {itemPrices} />
        </div>
      {/if}
    </div>
  </div>

  <ShareDraftModal org={$org} {job} {group} on:update-job={(e) => updateJob(e.detail)} />
  <ProductSendModal
    {job}
    {group}
    {items}
    {types}
    org={$org}
    on:update-job={(e) => updateJob(e.detail)}
    on:confirm={selfSend} />
  <DownloadModal title="Download Contents" {group} {types} name={job.name} />
  <HomeSettingsModal {group} {standardColumns} />
{/if}

<style lang="scss">
  .content-columns {
    display: flex;
    flex-direction: column;

    @media (min-width: 2000px) {
      flex-direction: row;
    }

    .left-column {
      @apply text-xs;
      display: flex;
      gap: 0.5rem;

      @media (min-width: 2000px) {
        display: block;
      }

      .content {
        flex-basis: calc(1 / 2 * 100%);
      }
    }

    .center-column {
      @apply grow;
    }
  }

  .price-computer {
    display: none;
    flex: none;

    @media (min-width: 1424px) {
      display: block;
      width: 20rem;
    }
  }
</style>
